/** @format */

import { useState, FormEvent } from "react";
import { SearchParams, Aggregations } from "@question-bank/shared";
import "./SearchForm.css";

interface SearchFormProps {
	onSearch: (params: SearchParams) => void;
	isLoading?: boolean;
	aggregations: Aggregations;
	selectedSubject?: string;
	selectedLevel?: string;
	selectedTier?: string;
}

export const SearchForm = ({
	onSearch,
	isLoading,
	aggregations,
	selectedSubject,
	selectedLevel,
	selectedTier,
}: SearchFormProps) => {
	const [subject, setSubject] = useState(selectedSubject || "");
	const [level, setLevel] = useState(selectedLevel || "");
	const [tier, setTier] = useState(selectedTier || "");

	// Sync props into local state when they change externally (e.g. tag click, facet click)
	const [prev, setPrev] = useState({ selectedSubject, selectedLevel, selectedTier });
	if (
		selectedSubject !== prev.selectedSubject ||
		selectedLevel !== prev.selectedLevel ||
		selectedTier !== prev.selectedTier
	) {
		setSubject(selectedSubject || "");
		setLevel(selectedLevel || "");
		setTier(selectedTier || "");
		setPrev({ selectedSubject, selectedLevel, selectedTier });
	}

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		onSearch({
			subject: subject || undefined,
			level: level || undefined,
			tier: tier || undefined,
			limit: 20,
			offset: 0,
		});
	};

	const handleReset = () => {
		setSubject("");
		setLevel("");
		setTier("");
		onSearch({ limit: 20, offset: 0 });
	};

	return (
		<form className="search-form" onSubmit={handleSubmit}>
			<div className="search-filters">
				<div className="filter-group">
					<label htmlFor="subject">Subject</label>
					<select id="subject" value={subject} onChange={(e) => setSubject(e.target.value)}>
						<option value="">All Subjects</option>
						{(aggregations.subject || []).map((agg) => (
							<option key={agg.key} value={agg.key}>
								{agg.key} ({agg.count.toLocaleString()})
							</option>
						))}
					</select>
				</div>

				<div className="filter-group">
					<label htmlFor="level">Level</label>
					<select id="level" value={level} onChange={(e) => setLevel(e.target.value)}>
						<option value="">All Levels</option>
						{(aggregations.level || []).map((agg) => (
							<option key={agg.key} value={agg.key}>
								{agg.key} ({agg.count.toLocaleString()})
							</option>
						))}
					</select>
				</div>

				<div className="filter-group">
					<label htmlFor="tier">Tier</label>
					<select id="tier" value={tier} onChange={(e) => setTier(e.target.value)}>
						<option value="">All Tiers</option>
						{(aggregations.tier || []).map((agg) => (
							<option key={agg.key} value={agg.key}>
								{agg.key} ({agg.count.toLocaleString()})
							</option>
						))}
					</select>
				</div>

				<div className="filter-actions">
					<button type="submit" className="search-button" disabled={isLoading}>
						{isLoading ? "Searching..." : "Apply Filters"}
					</button>
					<button type="button" className="reset-button" onClick={handleReset}>
						Reset Filters
					</button>
				</div>
			</div>
		</form>
	);
};
